import { QueryStringInput } from '@hello-aria/query-string'
import { isEmpty, isNil } from 'lodash'
import { Company } from '../models/companyModel'

import {
    CancelLoan,
    CreateLoanGateway,
    CreatePaymentRetry,
    CreatePaymentRetryGateway,
    DeleteLoan,
    Loan,
    LoanBlockReason,
    LoanGateway,
    UpdateLoanGateway,
    CreateLoanPayload,
    UpdateLoanPayload,
    editableLoanStatuses
} from '../models/loanModels'
import { batchFetch, resolveApiUrl } from '../utils/requestsUtils'
import { RequestResponse, requestService } from './requestServices'
import { handleHttpError } from '../utils/queryUtils'
import { downloadAttachments, uploadFileService } from './uploadServices'
import { mapPaymentDestination } from '@/modules/users/services/userServices'
import { mapInvoiceService } from './invoicesServices'
import { trackEvent } from '../utils/analyticsUtils'

interface GetLoans {
    companyId: string
    query?: QueryStringInput
    hasAttachments: boolean
    signal?: AbortSignal
}

export const getLoansService = batchFetch(async (params: GetLoans): Promise<RequestResponse<Loan[]> | undefined> => {
    const url = resolveApiUrl('loan.getAll', {
        variables: { companyId: params.companyId },
        query: {
            ...params.query,
            params: {
                ...params.query?.params,
                include: 'invoices'
            }
        }
    })

    const response = await requestService<LoanGateway[]>(
        url,
        params.signal
            ? {
                  signal: params.signal
              }
            : undefined
    )
    if (response?.body) {
        const mappedLoans = await Promise.all(response.body.map((loan) => mapLoanService(loan, params.hasAttachments)))
        const sanitizedMappedLoans = mappedLoans.filter((loan): loan is Loan => !isNil(loan))
        return { ...response, body: sanitizedMappedLoans }
    }
})

interface GetLoansByUserId {
    userId: string
    companyId: string
    query?: QueryStringInput
    hasAttachments: boolean
}

export const getLoansByUserIdService = async (
    params: GetLoansByUserId
): Promise<RequestResponse<Loan[]> | undefined> => {
    const url = resolveApiUrl('loan.getAll', {
        variables: { companyId: params.companyId },
        query: params.query
    })

    const operator = isNil(params.query) ? '?' : '&'

    const response = await requestService<LoanGateway[]>(
        `${url}${operator}include=invoices&filter=eq(userId,${params.userId})`
    )

    if (response?.body) {
        const mappedLoans = await Promise.all(
            response.body.map((invoice) => mapLoanService(invoice, params.hasAttachments))
        )
        const sanitizedMappedLoans = mappedLoans.filter((invoice): invoice is Loan => !isNil(invoice))
        return { ...response, body: sanitizedMappedLoans }
    }
}

interface GetLoanById {
    companyId: string
    loanId: string
}

export const getLoanByIdService = async (params: GetLoanById): Promise<RequestResponse<Loan> | undefined> => {
    const { companyId, loanId } = params
    const url = resolveApiUrl('loan.get', { variables: { companyId, loanId } })
    const response = await requestService<LoanGateway>(url)
    const mappedLoan = await mapLoanService(response?.body)

    return { ...response, body: mappedLoan }
}

interface GetLoansByInvoiceId {
    companyId: string
    invoiceId: string | undefined
}

export const getLoansByInvoiceIdService = async (
    params: GetLoansByInvoiceId
): Promise<RequestResponse<Loan[]> | undefined> => {
    const url = resolveApiUrl('loan.getAll', {
        query: { filters: [{ operator: 'eq', type: 'comparison', path: 'invoiceId', value: params.invoiceId }] },
        variables: { companyId: params.companyId }
    })
    const response = await requestService<LoanGateway[]>(url)
    if (response?.body) {
        const mappedLoans = await Promise.all(response.body.map((invoice) => mapLoanService(invoice, true)))
        const sanitizedMappedLoans = mappedLoans.filter((invoice): invoice is Loan => !isNil(invoice))
        return { ...response, body: sanitizedMappedLoans }
    }
}

export const createLoanService = async (
    companyId: string,
    payload: CreateLoanPayload
): Promise<RequestResponse<Loan> | undefined> => {
    try {
        const url = resolveApiUrl('loan.create', { variables: { companyId } })

        const files = await uploadFileService(payload.attachments, companyId)
        const formattedData: CreateLoanGateway = {
            amount: payload.amount,
            currency: payload.currency,
            invoiceId: payload.invoiceId,
            receivableId: payload.receivableId,
            userId: payload.userId,
            payoutLabel: payload.payoutLabel || undefined,
            attachments: files,
            paymentDestination: payload.paymentDestination
                ? mapPaymentDestination(payload.paymentDestination)
                : undefined,
            guarantor: payload.guarantor
        }

        const response = await requestService<Loan>(url, { method: 'POST', data: formattedData })
        trackEvent('Create_Loan')
        return response
    } catch (error) {
        handleHttpError('createLoanService', error)
    }
}

export const updateLoanService = async (
    companyId: string,
    payload: UpdateLoanPayload
): Promise<RequestResponse<Loan> | undefined> => {
    const url = resolveApiUrl('loan.update', { variables: { companyId, loanId: payload.loanId } })

    try {
        const files = await uploadFileService(payload.attachments, companyId)
        const formattedData: UpdateLoanGateway = {
            amount: payload.amount,
            userId: payload.userId,
            currency: payload.currency,
            invoiceId: payload.invoiceId,
            receivableId: payload.receivableId,
            payoutLabel: payload.payoutLabel || undefined,
            attachments: files,
            paymentDestination: payload.paymentDestination
                ? mapPaymentDestination(payload.paymentDestination)
                : undefined,
            guarantor: payload.guarantor
        }

        const response = await requestService<Loan>(url, { method: 'PATCH', data: formattedData })
        trackEvent('Update_Loan')
        const mappedLoan = await mapLoanService(response?.body)
        return { ...response, body: mappedLoan }
    } catch (error) {
        handleHttpError('updateLoanService', error)
    }
}

export const cancelLoanService = async (payload: CancelLoan): Promise<RequestResponse<Loan> | undefined> => {
    const { companyId, loanId } = payload

    if (isNil(loanId) || isNil(companyId)) {
        throw new Error(`cancelLoanService : Missing companyId "${companyId}" or loanId: "${loanId}"`)
    }

    const url = resolveApiUrl('loan.cancel', { variables: { companyId, loanId } })
    const response = await requestService<Loan>(url, { method: 'POST' })
    trackEvent('Update_Loan_Cancel')
    return response
}

export const deleteLoanService = async (payload: DeleteLoan): Promise<RequestResponse<Loan> | undefined> => {
    const { companyId, loanId } = payload
    const url = resolveApiUrl('loan.delete', { variables: { companyId, loanId } })
    try {
        const response = await requestService<Loan>(url, { method: 'DELETE' })
        trackEvent('Delete_Loan')
        return response
    } catch (error) {
        handleHttpError('deleteLoanService', error)
    }
}

const mapLoanService = async (data: LoanGateway | undefined, hasAttachments = true): Promise<Loan | undefined> => {
    if (isNil(data)) return undefined

    const invoice = await mapInvoiceService(data.invoice, false)

    if (isNil(data.attachments) || isEmpty(data.attachments) || !hasAttachments) {
        return {
            ...data,
            attachments: undefined,
            invoice,
            isEditable: editableLoanStatuses.includes(data.status)
        }
    }

    const attachments = await downloadAttachments(data.attachments)
    const attachmentCount = attachments?.length ?? 0
    return {
        ...data,
        attachments: isEmpty(attachments) ? undefined : attachments,
        attachmentCount,
        invoice,
        isEditable: editableLoanStatuses.includes(data.status)
    }
}

export const getLoanBlockReasonsService = async (
    loanId: Loan['id'] | undefined
): Promise<RequestResponse<LoanBlockReason[]> | undefined> => {
    if (isNil(loanId)) return
    const url = resolveApiUrl('loanBlockedReasons.get', {
        variables: { loanId },
        query: { pagination: { first: 100 } }
    })
    return requestService<LoanBlockReason[]>(url)
}

export const createLoanPaymentRetryService = async (
    payload: CreatePaymentRetry,
    companyId: Company['id']
): Promise<RequestResponse<Loan> | undefined> => {
    const { loanId, paymentDestination } = payload
    const url = resolveApiUrl('loan.paymentRetry', { variables: { companyId, loanId } })
    const formattedPayload: CreatePaymentRetryGateway = {
        options: {
            preferInstantPayment: payload.preferInstantPayment,
            paymentDestination: {
                accountNumber: paymentDestination.accountNumber,
                accountNumberType: paymentDestination.accountNumberType,
                routingNumber: paymentDestination.routingNumber || undefined,
                routingNumberType: paymentDestination.accountNumberType === 'IBAN' ? 'BIC' : 'SORT_CODE',
                type: 'BANK_ACCOUNT'
            }
        }
    }
    const response = await requestService<Loan>(url, { method: 'POST', data: formattedPayload })
    trackEvent('Update_Loan_Retry')
    return response
}
